import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Wallet, RefreshCw, IndianRupee, Download } from 'lucide-react';

export default function Payroll() {
  const [payroll, setPayroll] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  useEffect(() => {
    fetchPayroll();
  }, [month]);

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll?month=${month}`);
      setPayroll(res.data);
    } catch (error) {
      console.error('Failed to load payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/payroll/generate', { month });
      toast.success(`Generated ${res.data.total_generated} payslips`);
      fetchPayroll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payroll');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (v) => {
    if (v === null || v === undefined) return 'Rs. 0';
    return `Rs. ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const downloadPayslipPDF = async (payslipId, empName, month) => {
    try {
      const res = await api.get(`/payroll/${payslipId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip_${empName.replace(/\s+/g, '_')}_${month}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Payroll</h1>
          <p className="text-slate-500">Generate and manage monthly salary payslips</p>
        </div>
        <div className="flex gap-3 items-center">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[180px]" data-testid="payroll-month-input" />
          <Button onClick={handleGenerate} disabled={generating} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="generate-payroll-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating...' : 'Generate'}
          </Button>
        </div>
      </div>

      {/* Summary */}
      {payroll && payroll.payslips?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Total Employees</p>
              <p className="text-2xl font-bold">{payroll.payslips.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Total Gross</p>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(payroll.total_gross)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Total Net Payable</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(payroll.total_net)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card className="bg-white border shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="payroll-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Employee</TableHead>
                <TableHead className="font-semibold">Code</TableHead>
                <TableHead className="font-semibold">Days Present</TableHead>
                <TableHead className="font-semibold">Gross</TableHead>
                <TableHead className="font-semibold">Deductions</TableHead>
                <TableHead className="font-semibold">Net Salary</TableHead>
                <TableHead className="font-semibold text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : !payroll?.payslips?.length ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No payslips for this month. Click "Generate" to create them.</TableCell></TableRow>
              ) : (
                payroll.payslips.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.employee_name}</TableCell>
                    <TableCell className="font-mono text-[#FF9933]">{p.employee_code}</TableCell>
                    <TableCell>{p.days_present}/{p.total_working_days}</TableCell>
                    <TableCell>{formatCurrency(p.gross_salary)}</TableCell>
                    <TableCell className="text-red-500">{formatCurrency(p.total_deductions)}</TableCell>
                    <TableCell className="text-green-600 font-bold">{formatCurrency(p.net_salary)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setSelectedPayslip(p)} data-testid={`view-payslip-${p.id}`}>
                          View
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadPayslipPDF(p.id, p.employee_name, p.month)} data-testid={`download-payslip-${p.id}`}>
                          <Download className="w-4 h-4 text-slate-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payslip Detail Dialog */}
      <Dialog open={!!selectedPayslip} onOpenChange={() => setSelectedPayslip(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5" /> Payslip - {selectedPayslip?.month}</DialogTitle>
            <DialogDescription>{selectedPayslip?.employee_name} ({selectedPayslip?.employee_code})</DialogDescription>
          </DialogHeader>
          {selectedPayslip && (
            <div className="space-y-4 text-sm">
              <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                <p className="font-semibold text-slate-700 mb-2">Attendance</p>
                <div className="flex justify-between"><span>Working Days</span><span>{selectedPayslip.total_working_days}</span></div>
                <div className="flex justify-between"><span>Days Present</span><span className="text-green-600">{selectedPayslip.days_present}</span></div>
                <div className="flex justify-between"><span>Paid Leaves</span><span className="text-blue-600">{selectedPayslip.paid_leave_days}</span></div>
                <div className="flex justify-between"><span>Unpaid Leaves</span><span className="text-orange-600">{selectedPayslip.unpaid_leave_days}</span></div>
                <div className="flex justify-between"><span>Absent</span><span className="text-red-500">{selectedPayslip.days_absent}</span></div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg space-y-1">
                <p className="font-semibold text-green-800 mb-2">Earnings</p>
                <div className="flex justify-between"><span>Basic Salary</span><span>{formatCurrency(selectedPayslip.salary_basic)}</span></div>
                <div className="flex justify-between"><span>DA (Dearness Allowance)</span><span>{formatCurrency(selectedPayslip.salary_da)}</span></div>
                <div className="flex justify-between"><span>HRA (House Rent)</span><span>{formatCurrency(selectedPayslip.salary_hra)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>Gross Salary</span><span>{formatCurrency(selectedPayslip.gross_salary)}</span></div>
              </div>
              <div className="p-3 bg-red-50 rounded-lg space-y-1">
                <p className="font-semibold text-red-800 mb-2">Deductions</p>
                <div className="flex justify-between"><span>PF (12%)</span><span>{formatCurrency(selectedPayslip.deduction_pf)}</span></div>
                <div className="flex justify-between"><span>ESI (0.75%)</span><span>{formatCurrency(selectedPayslip.deduction_esi)}</span></div>
                <div className="flex justify-between"><span>TDS (10%)</span><span>{formatCurrency(selectedPayslip.deduction_tds)}</span></div>
                <div className="flex justify-between"><span>Absence Deduction</span><span>{formatCurrency(selectedPayslip.deduction_absence)}</span></div>
                <div className="flex justify-between"><span>Unpaid Leave Ded.</span><span>{formatCurrency(selectedPayslip.deduction_unpaid_leave)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>Total Deductions</span><span>{formatCurrency(selectedPayslip.total_deductions)}</span></div>
              </div>
              <div className="p-4 bg-[#FF9933]/10 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-slate-800">Net Salary</span>
                  <span className="text-xl font-bold text-green-600">{formatCurrency(selectedPayslip.net_salary)}</span>
                </div>
              </div>
              <Button className="w-full bg-[#FF9933] hover:bg-[#E68A2E]" onClick={() => downloadPayslipPDF(selectedPayslip.id, selectedPayslip.employee_name, selectedPayslip.month)} data-testid="download-payslip-pdf-btn">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
